'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { AuthSessionInfo } from '@/app/api/auth/_lib/types';
import {
  completeEmbeddedAuth,
  getAuthSession,
  reportAuthSessionFailure,
  startEmbeddedAuth,
} from '@/app/utils/auth-session-api';
import {
  clearModernAuthApproval,
  currentModernAuthApproval,
  modernAuthCompletionEvent,
  modernAuthEventProperties,
} from '@/app/utils/auth-analytics';
import { useTracking } from '@/components/tracking-provider';
import AuthInteractionScreen from './interactions/interaction-screen';
import type { AuthInteractionMode, InteractionCompletion } from './interactions/types';

type EmbeddedAuthState =
  | { kind: 'loading' }
  | { kind: 'ready'; interactionToken: string }
  | { kind: 'authenticated'; session: AuthSessionInfo }
  | { kind: 'error'; message: string };

export default function EmbeddedAuth({
  initialMode = 'login',
  returnTo = '/',
  onAuthenticated,
  renderAuthenticated,
}: {
  initialMode?: AuthInteractionMode;
  returnTo?: string;
  onAuthenticated?: (session: AuthSessionInfo) => void;
  renderAuthenticated?: (session: AuthSessionInfo) => React.ReactNode;
}) {
  const tracker = useTracking();
  const [state, setState] = useState<EmbeddedAuthState>({ kind: 'loading' });
  const mounted = useRef(true);
  const requestGeneration = useRef(0);
  const initializationPromise = useRef<Promise<void> | null>(null);
  const onAuthenticatedRef = useRef(onAuthenticated);

  useEffect(() => {
    onAuthenticatedRef.current = onAuthenticated;
  }, [onAuthenticated]);

  useEffect(() => {
    // Strict Mode runs setup -> cleanup -> setup in development. Restore the
    // ref during setup and invalidate any async work from the prior setup.
    mounted.current = true;
    return () => {
      mounted.current = false;
      requestGeneration.current += 1;
    };
  }, []);

  const acceptSession = useCallback(
    (session: AuthSessionInfo, completed: boolean = false) => {
      if (!mounted.current) return;
      requestGeneration.current += 1;
      setState({ kind: 'authenticated', session });
      if (completed) {
        const approval = currentModernAuthApproval();
        if (approval?.completionMode === 'embedded') {
          tracker.setUser(session.user.id);
          tracker.trackEvent(
            modernAuthCompletionEvent(approval.flow),
            modernAuthEventProperties(approval),
          );
          clearModernAuthApproval();
        }
      }
      onAuthenticatedRef.current?.(session);
    },
    [tracker],
  );

  const start = useCallback(async () => {
    const generation = requestGeneration.current + 1;
    requestGeneration.current = generation;
    setState({ kind: 'loading' });
    try {
      const interactionToken = await startEmbeddedAuth(returnTo);
      if (mounted.current && generation === requestGeneration.current) {
        setState({ kind: 'ready', interactionToken });
      }
    } catch (error) {
      reportAuthSessionFailure(error, { source: 'embedded-auth', operation: 'start' });
      if (mounted.current && generation === requestGeneration.current) {
        setState({
          kind: 'error',
          message: 'Modern authentication is temporarily unavailable. Please try again.',
        });
      }
    }
  }, [returnTo]);

  const initialize = useCallback(async () => {
    try {
      const session = await getAuthSession();
      if (session) {
        acceptSession(session);
        return;
      }
    } catch (error) {
      reportAuthSessionFailure(error, { source: 'embedded-auth', operation: 'session' });
      // A bootstrap failure is recoverable by starting a fresh transaction.
    }

    // A real unmount must not start a transaction after the component is gone.
    // During Strict Mode's development-only setup -> cleanup -> setup cycle,
    // the second setup restores this ref before the promise can resume.
    if (mounted.current) {
      await start();
    }
  }, [acceptSession, start]);

  useEffect(() => {
    // Strict Mode re-runs effects in development. Reuse the initial request so
    // its second setup does not issue a second /api/auth/session call.
    if (!initializationPromise.current) {
      initializationPromise.current = initialize();
    }
  }, [initialize]);

  const completion: InteractionCompletion = {
    mode: 'embedded',
    complete: async (resumeUrl) => {
      const session = await completeEmbeddedAuth(resumeUrl);
      acceptSession(session, true);
    },
  };

  if (state.kind === 'loading') {
    return (
      <div className="py-10 text-center" role="status">
        <div className="mx-auto mb-3 h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        <p className="text-sm text-slate-600">Preparing secure authentication...</p>
      </div>
    );
  }

  if (state.kind === 'error') {
    return (
      <div className="py-6 text-center">
        <h2 className="mb-2 font-merriweather text-2xl font-bold">Authentication unavailable</h2>
        <p className="mb-5 text-sm text-slate-600">{state.message}</p>
        <button type="button" className="btn w-full text-white bg-blue-600" onClick={start}>
          Try again
        </button>
      </div>
    );
  }

  if (state.kind === 'authenticated') {
    return renderAuthenticated ? (
      renderAuthenticated(state.session)
    ) : (
      <div className="py-6 text-center">
        <h2 className="mb-2 font-merriweather text-2xl font-bold">You&apos;re signed in</h2>
        <p className="text-sm text-slate-600">Signed in as {state.session.user.email}.</p>
      </div>
    );
  }

  return (
    <AuthInteractionScreen
      key={state.interactionToken}
      interactionToken={state.interactionToken}
      completion={completion}
      onRestart={() => void start()}
      initialMode={initialMode}
    />
  );
}
